import { DataRefreshEvent } from "@fp/admin-api-client";
import { listStaffMembers, removeStaffMember } from "@fp/admin-api-client/client";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { ConfirmationModal } from "@fp/ui/components/application/modals/confirmation-modal";
import { PageContentContainer } from "@fp/ui/components/base/page-layout/page-layout";
import { StaffList } from "@fp/ui/components/staff/staff-list";
import { formatDisplayName } from "@fp/ui/utils/name";
import { toast } from "sonner";

import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { useDataRefresh } from "@/hooks/useSignalR";

export const Route = createFileRoute("/staff/")({
	loader: async () => {
		const staff = await listStaffMembers({ page: 1, pageSize: 50 });
		const sortedStaff = [...(staff.data.items ?? [])].sort((a, b) => {
			const lastNameCompare = (a.lastName ?? "").localeCompare(
				b.lastName ?? "",
			);
			if (lastNameCompare !== 0) return lastNameCompare;
			return (a.firstName ?? "").localeCompare(b.firstName ?? "");
		});
		return {
			staffMembers: sortedStaff,
		};
	},
	component: RouteComponent,
});

function RouteComponent() {
	const data = Route.useLoaderData();
	const router = useRouter();
	const [isRemoving, setIsRemoving] = useState(false);
	const [deleteModalStaff, setDeleteModalStaff] = useState<{ id: string; name: string } | null>(null);

	const invalidateStaff = useCallback(() => { router.invalidate(); }, [router]);
	useDataRefresh(DataRefreshEvent.InvitationCreated, invalidateStaff);
	useDataRefresh(DataRefreshEvent.JoinRequestApproved, invalidateStaff);
	useDataRefresh(DataRefreshEvent.StaffMemberJoined, invalidateStaff);

	const onStaffNavigate = useCallback(
		(staffId: string) => {
			router.navigate({
				to: "/staff/$id/edit",
				params: { id: staffId },
			});
		},
		[router],
	);

	const onNewStaff = () => {
		router.navigate({ to: "/invitations/new" });
	};

	const onRemove = useCallback(
		(staffId: string) => {
			const staff = data.staffMembers.find((s) => s.id === staffId);
			if (!staff) return;
			setDeleteModalStaff({ id: staffId, name: formatDisplayName(staff) || 'this member' });
		},
		[data.staffMembers],
	);

	const handleRemoveConfirm = useCallback(async () => {
		if (!deleteModalStaff) return;

		setIsRemoving(true);
		try {
			await removeStaffMember(deleteModalStaff.id);
			toast.success(`${deleteModalStaff.name} has been removed`);
			setDeleteModalStaff(null);
			router.invalidate();
		} catch (error) {
			toast.error("Failed to remove staff member. Please try again.");
		} finally {
			setIsRemoving(false);
		}
	}, [deleteModalStaff, router]);

	return (
		<AdminLayout>
			<AdminPageHeader
				title="Staff Members"
				parent={{ url: "/settings", label: "Settings" }}
			/>
			<PageContentContainer className="space-y-6 bg-white p-0">
				<StaffList
					staffMembers={data.staffMembers}
					onStaffNavigate={onStaffNavigate}
					onNewStaff={onNewStaff}
					onStaffUpdate={async () => {
						router.invalidate();
					}}
					onRemove={onRemove}
					isLoading={isRemoving}
				/>
			</PageContentContainer>
			<ConfirmationModal
				isOpen={!!deleteModalStaff}
				onClose={() => setDeleteModalStaff(null)}
				onConfirm={handleRemoveConfirm}
				title="Remove Staff Member"
				message={`Are you sure you want to remove ${deleteModalStaff?.name} from your organization?`}
				confirmText="Remove"
				cancelText="Cancel"
				isLoading={isRemoving}
			/>
		</AdminLayout>
	);
}
